/**
 * SCST 솔루션 상세 페이지 — 진단보조
 *
 * PO 원칙 준수:
 * - 원칙1: 영역별 표준화 점수 제공. 자동 등급 산출 없음. 임상 판정은 담당의 영역.
 * - 원칙4: 클라우드(의원·중소병원) / 온프레미스(대형병원·종합병원) 양 트랙 명시.
 * - 원칙5: 영역별 표준화 점수 표시.
 * 핵심: 신의료기술 통과(2026.5) 사실 기반 강조.
 */
import { createFileRoute } from '@tanstack/react-router';
import React from 'react';
import { PublicLayout } from '../../components/public-layout.js';
import { SolutionLayout } from '../../components/solution-layout.js';
import { SectionBlock } from '../../components/section-block.js';

const S: React.CSSProperties = { padding: '100px 0', borderTop: '1px solid var(--line)' };

/** 푸터 고지 카드에 더하는 페이지 항목(공통 항목과 라벨 기준 병합) */
const DISCLAIMER = [
  {
    label: 'SCST 사용 목적',
    text: 'SCST는 신경심리검사 보조 도구로, 영역별 표준화 점수를 제공합니다. 정상/경도인지장애/치매 등 임상 판정은 담당의 종합 판단에 따릅니다. 본 페이지의 일부 수치는 실제 데이터로 교체 예정인 안내용 자료를 포함합니다.',
  },
  {
    label: '책임의 분리',
    text: '진료 행위 및 치료 결과에 대한 최종 책임은 담당의 및 해당 의료기관에 있습니다. BeauBrain은 의료기관에 도구를 공급하는 의료기기·서비스 사업자입니다.',
  },
];

function Page() {
  return (
    <PublicLayout disclaimerItems={DISCLAIMER}>
      <SolutionLayout
        stage="dx"
        badgeLabel="DX · 진단보조 · 신의료기술 통과 ✓"
        name="SCST"
        tagline="의료기관에서 정식으로 시행하는 신경심리검사 보조 도구. 5개 핵심 인지 영역의 표준화 점수를 산출하여 담당의의 종합 판단을 지원합니다."
        domain="신의료기술 평가 통과 (2026.5) · 신경심리검사 보조 · 30~40분"
        crossSell={[
          {
            href: '/solutions/mmse',
            name: 'MMSE',
            desc: 'SCST 시행 전 단계 — 5분 간이 인지 선별 검사',
            stage: 'scr',
          },
          {
            href: '/solutions/amylo',
            name: 'Amylo',
            desc: 'ATN의 A · Amyloid PET Centiloid 정량',
            stage: 'img',
          },
          {
            href: '/solutions/pascal-med',
            name: 'PASCAL-Med',
            desc: '의료기관용 디지털 인지 중재 솔루션',
            stage: 'int',
          },
        ]}
      >

        {/* ── 신의료기술 통과 강조 배너 ─────────────────────────── */}
        <section style={S}>
          <div className="container">
            {/* 통과 사실 카드 — 열·여백은 public.css .scst-cert-banner(980px 이하 1단) */}
            <div
              className="scst-cert-banner"
              style={{
                background: 'linear-gradient(135deg, rgba(52,211,153,0.12) 0%, rgba(16,185,129,0.08) 100%)',
                border: '1px solid rgba(52,211,153,0.35)',
                borderRadius: 24,
                marginBottom: 56,
              }}
            >
              <div>
                <div
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    background: '#10B981', color: 'white',
                    padding: '8px 18px', borderRadius: 999,
                    fontSize: 13, fontWeight: 700, marginBottom: 20,
                    boxShadow: '0 8px 24px rgba(16,185,129,0.3)',
                  }}
                >
                  ✓ 신의료기술 평가 통과 · 2026년 5월
                </div>
                <h2
                  className="scst-cert-title"
                  style={{
                    fontWeight: 800, color: 'var(--ink)',
                    letterSpacing: '-0.03em', lineHeight: 1.2, marginBottom: 16,
                  }}
                >
                  SCST는 2026년 5월,<br />정식 진단보조 도구로 통과되었습니다.
                </h2>
                <p style={{ fontSize: 15, color: 'var(--ink-3)', lineHeight: 1.7 }}>
                  한국보건의료연구원(NECA) 신의료기술 평가를 통과하여 안전성과 유효성이 인정된
                  신경심리검사 보조 도구입니다. 의원·중소병원·종합병원 모두 정식 사용 가능합니다.
                </p>
              </div>
              <div
                style={{
                  background: 'var(--surface-2)',
                  border: '1px solid var(--line-2)',
                  borderRadius: 16, padding: '28px 32px',
                  display: 'flex', flexDirection: 'column', gap: 14,
                  minWidth: 220,
                }}
              >
                {[
                  { label: '통과 시점', value: '2026.05' },
                  { label: '분류', value: '신경심리검사 보조' },
                  { label: '적용', value: '의원 · 병원 · 종합병원' },
                ].map(({ label, value }) => (
                  <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 600, letterSpacing: '0.05em' }}>{label}</span>
                    <span className="num" style={{ fontSize: 14, color: 'var(--green-fg)', fontWeight: 700 }}>{value}</span>
                  </div>
                ))}
                <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8, lineHeight: 1.6 }}>
                  ※ 상세 인허가 자료는 계약 의료기관에 별도 제공됩니다.{' '}
                  <span style={{ background: 'rgba(245,158,11,0.15)', color: 'var(--amber-fg)', padding: '1px 5px', borderRadius: 3, fontSize: 10 }}>
                    &lt;실제 데이터로 교체&gt;
                  </span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 통과의 의미 ───────────────────────────────────────── */}
        <section style={{ ...S, background: 'var(--bg-surface)' }}>
          <div className="container">
            <SectionBlock
              label="What it means"
              title="신의료기술 통과가 의료기관에 의미하는 것"
              subtitle='"의료기관에서 정식으로 진단보조를 시행할 수 있다"는 것이 진료 현장에서 어떻게 작동하는지 정리했습니다.'
            >
              <div className="apple-bento" style={{ marginTop: 56 }}>
                <div className="b-item">
                  <span className="b-eyebrow">MEANING 01</span>
                  <div className="b-title">의료기관에서 직접 시행</div>
                  <p className="b-desc">
                    신경심리검사를 위해 상급종합병원으로 환자를 보내지 않아도 됩니다.
                    의원·중소병원에서 직접 시행하고 결과를 환자에게 즉시 설명할 수 있습니다.
                  </p>
                </div>
                <div className="b-item">
                  <span className="b-eyebrow">MEANING 02</span>
                  <div className="b-title">진단 흐름의 연속성</div>
                  <p className="b-desc">
                    스크리닝(MMSE) 이후 정밀 검사가 필요한 환자에게, 별도 검사 의뢰 없이
                    의료기관 안에서 진단 흐름을 이어갈 수 있습니다.
                  </p>
                </div>
                <div className="b-item">
                  <span className="b-eyebrow">MEANING 03</span>
                  <div className="b-title">정식 진단보조의 신뢰성</div>
                  <p className="b-desc">
                    정식 인허가된 진단보조 도구를 사용한다는 것 자체가,
                    환자와 보호자에게 진료의 신뢰성을 전달합니다.
                  </p>
                </div>
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── 진료 흐름 ─────────────────────────────────────────── */}
        <section style={S}>
          <div className="container">
            <SectionBlock
              label="Clinical Workflow"
              title="진료실에서 어떻게 작동하는가"
              subtitle="스크리닝에서 인지 저하가 의심된 환자에게 SCST를 시행하는 실제 진료 흐름입니다."
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 56, maxWidth: 760, marginInline: 'auto' }}>
                {[
                  {
                    n: 1,
                    title: 'SCST 시행 결정',
                    desc: 'MMSE 결과를 바탕으로 담당의가 정밀 진단보조 시행을 결정합니다. "의료기관에서 정식 검사를 진행할 수 있다"는 안내로 환자에게 안심감을 제공합니다.',
                  },
                  {
                    n: 2,
                    title: '디지털 신경심리검사 진행 (30~40분)',
                    desc: '환자가 의료기관 태블릿에서 약 30~40분간 검사를 진행합니다. 주의력·기억력·언어·시공간·전두엽 기능 등 5개 핵심 인지 영역을 표준화된 도구로 평가합니다.',
                  },
                  {
                    n: 3,
                    title: '영역별 표준화 점수 리포트 생성',
                    desc: '검사 종료 즉시 영역별 표준화 점수와 정량 리포트가 의사 대시보드에 표시됩니다. 정상/경도인지장애/치매 등 진단 단계의 임상 판정은 담당의 종합 판단에 따릅니다.',
                  },
                  {
                    n: 4,
                    title: '환자·보호자에게 결과 설명',
                    desc: '의사가 리포트를 보면서 환자에게 결과를 설명합니다. 그래프와 영역별 점수로 시각화되어 있어 의학 지식이 없는 환자·보호자도 이해하기 쉽습니다.',
                  },
                  {
                    n: 5,
                    title: '다음 진료 단계 의사 판단',
                    desc: 'SCST 결과와 MMSE 결과를 종합하여 담당의가 인지중재(PASCAL-Med) 또는 영상 정밀 검사(Amylo·Morph) 등 다음 단계를 임상적으로 판단합니다.',
                  },
                ].map(({ n, title, desc }) => (
                  <div key={n} className="b-item" style={{ display: 'flex', gap: 20, padding: '24px 28px', borderRadius: 14 }}>
                    <div
                      style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: 'rgba(59,130,246,0.2)', color: 'var(--blue-fg)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 800, fontSize: 13, flexShrink: 0, fontFamily: "'Inter Tight', sans-serif",
                      }}
                    >
                      {n}
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>{title}</div>
                      <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.7 }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── 임상 근거 ─────────────────────────────────────────── */}
        <section style={{ ...S, background: 'var(--bg-surface)' }}>
          <div className="container">
            <SectionBlock
              label="Clinical Evidence"
              title={<>30년 임상 자산이<br />받쳐주는 진단보조.</>}
              subtitle="SCST는 삼성서울병원 신경과 30년 임상 데이터를 기반으로 설계되었고, 신의료기술 평가를 통해 정식 진단보조 도구로 인정받았습니다."
            >
              <div className="cols-2" style={{ gap: 20, marginTop: 56 }}>
                <div className="b-item">
                  <span className="b-eyebrow">Clinical Foundation</span>
                  <div className="b-title">삼성서울병원 신경과 30년 임상 자산</div>
                  <p className="b-desc" style={{ marginBottom: 24 }}>나덕렬·서상원 교수 등 신경과 전문의들의 임상 경험 기반</p>
                  <div className="cols-3 tight" style={{ gap: 12 }}>
                    {[
                      { n: '30년', l: '임상 경험' },
                      { n: '5', l: '핵심 인지 영역' },
                      { n: <span style={{ fontSize: 11 }}>&lt;실제 데이터로 교체&gt;</span>, l: '누적 환자 사례' },
                    ].map(({ n, l }, i) => (
                      <div key={i} style={{ background: 'var(--surface-2)', border: '1px solid var(--line-2)', borderRadius: 10, padding: 16, textAlign: 'center' }}>
                        <div className="num" style={{ fontSize: 28, fontWeight: 800, color: 'var(--accent)', lineHeight: 1, marginBottom: 6 }}>{n}</div>
                        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{l}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="b-item">
                  <span className="b-eyebrow">Regulation</span>
                  <div className="b-title">신의료기술 평가 통과 (2026.5)</div>
                  <p className="b-desc" style={{ marginBottom: 20 }}>
                    정식 의료기술로 인정. 의원·중소병원·종합병원 모두 적용 가능.
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    {['통과 2026.05', '신경심리검사 보조', '의원·중소병원·종합병원'].map((t) => (
                      <span
                        key={t}
                        style={{
                          padding: '8px 12px', background: 'var(--surface-3)',
                          border: '1px solid var(--line-3)',
                          borderRadius: 8, fontSize: 12, fontWeight: 600, color: 'var(--ink-2)',
                        }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.6 }}>
                    양 트랙 지원: 1·2차 의료기관(의원·중소병원)은 클라우드 SaaS로 즉시 도입,
                    대형 병원·종합병원은 온프레미스 구축 옵션도 제공합니다.
                  </p>
                </div>
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── 핵심 기능 ─────────────────────────────────────────── */}
        <section style={S}>
          <div className="container">
            <SectionBlock label="Features" title="핵심 기능">
              <div className="cols-3" style={{ gap: 16, marginTop: 56 }}>
                {[
                  { title: '5개 핵심 인지 영역', desc: '주의력·기억력·언어·시공간·전두엽 기능을 표준화된 도구로 평가합니다.' },
                  { title: '영역별 표준화 점수 산출', desc: '영역별 정량 점수를 자동 채점하여 의사의 종합 판단을 지원합니다. 정상/MCI/치매 등 임상 판정은 담당의 영역입니다.' },
                  { title: '표준화된 리포트', desc: '영역별 점수 시각화 + 환자/보호자 설명용 일반인 리포트를 동시 생성합니다.' },
                  { title: 'MMSE 결과 자동 연동', desc: '동일 환자의 MMSE 결과를 자동으로 가져와 의사의 종합 판단을 지원합니다.' },
                  { title: '양 트랙 도입 지원', desc: '의원·중소병원은 클라우드 SaaS로 즉시 도입. 대형병원·종합병원은 온프레미스 구축 옵션도 제공합니다.' },
                  { title: '진료 기록 자동 보관', desc: '검사 결과·리포트가 BeauBrain 통합 계정에 자동 저장됩니다. 재방문 시 시기별 변화 추적이 가능합니다.' },
                ].map(({ title, desc }) => (
                  <div key={title} className="b-item" style={{ padding: '28px 24px' }}>
                    <span className="sol-h-badge dx" style={{ marginBottom: 14 }}>DX</span>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>{title}</div>
                    <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>{desc}</div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────── */}
        <section style={{ ...S, background: 'var(--bg-surface)' }}>
          <div className="container">
            <SectionBlock label="FAQ" title="자주 묻는 질문">
              <div style={{ maxWidth: 800, margin: '56px auto 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  {
                    q: '신의료기술 통과란 정확히 무엇인가요?',
                    a: '한국보건의료연구원(NECA)의 신의료기술 평가를 통과했다는 의미로, 안전성과 유효성이 인정되어 의료현장에서 정식으로 사용할 수 있게 되었음을 뜻합니다. SCST는 2026년 5월 통과되어 신경심리검사 보조 도구로 정식 사용 가능합니다.',
                  },
                  {
                    q: '어떤 의료기관에서 사용 가능한가요?',
                    a: '의원·중소병원·종합병원 모두 사용 가능합니다. 1·2차 의료기관(의원·중소병원)에서는 클라우드 SaaS로 즉시 도입할 수 있고, 대형 병원·종합병원에서는 보안·내부 규정에 맞춰 온프레미스 구축 옵션도 제공합니다.',
                  },
                  {
                    q: '검사 시간이 30~40분이면 진료 흐름에 부담이 되지 않을까요?',
                    a: '환자가 태블릿으로 자기 주도적으로 검사를 진행하므로, 의사·간호사의 직접 시간 투입은 검사 안내(5분 내외)와 결과 설명(10분 내외)에 한정됩니다. 검사 중에는 다른 환자 진료가 가능합니다.',
                  },
                  {
                    q: '검사 결과만으로 치매 진단이 가능한가요?',
                    a: 'SCST는 진단보조 도구로, 최종 진단은 의사의 종합 판단에 따릅니다. 인지 영역별 표준화 점수를 제공하여 의사의 진단 결정을 지원하는 역할입니다. 필요시 영상 검사(Amylo·Morph)와 병행이 검토될 수 있습니다.',
                  },
                ].map(({ q, a }) => (
                  <div key={q} className="b-item" style={{ padding: '24px 28px', borderRadius: 14 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 10, display: 'flex', gap: 12 }}>
                      <span className="num" style={{ color: 'var(--accent)', fontWeight: 800, flexShrink: 0 }}>Q.</span>
                      {q}
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.7, paddingLeft: 28 }}>{a}</div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          </div>
        </section>

      </SolutionLayout>
    </PublicLayout>
  );
}

export const Route = createFileRoute('/solutions/scst')({
  component: Page,
});
